import { ApiProjectSettingKey } from "@incendium/api";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { fetchAllClientsAndProjectsThunk } from "Actions";
import {
  resolveScriptDomainOptionId,
  useFireflyScriptDomainOptions,
} from "features/project/hooks/useFireflyScriptDomainOptions";
import { saveProjectSetting } from "features/project/services/projectSettingsService";
import { TProjectSettings, useNotification, useSelectedProject } from "Hooks";
import produce from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

const SCRIPT_STRATEGIES = ["async", "defer", "none"] as const;
const SCRIPT_POSITIONS = ["head", "footer"] as const;

interface IWooCommercePluginSettingsProps {
  onSaved?: () => void;
}

function WooCommercePluginSettings({
  onSaved,
}: IWooCommercePluginSettingsProps) {
  const dispatch = useDispatch();
  const { selectedProject, projectSettings, setProjectSettings } =
    useSelectedProject();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { scriptDomainOptions, defaultScriptBaseUrl } =
    useFireflyScriptDomainOptions();
  const [saving, setSaving] = useState(false);
  const [selectedUrlSource, setSelectedUrlSource] = useState("default");
  const [scriptStrategy, setScriptStrategy] = useState<string>("async");
  const [scriptPosition, setScriptPosition] = useState<string>("head");
  const [serverSideTracking, setServerSideTracking] = useState(false);

  const storedBaseUrl =
    projectSettings[ApiProjectSettingKey.WOOCOMMERCE_SCRIPT_BASE_URL];

  useEffect(() => {
    setSelectedUrlSource(
      resolveScriptDomainOptionId(storedBaseUrl, scriptDomainOptions),
    );
    const strategy =
      projectSettings[ApiProjectSettingKey.WOOCOMMERCE_SCRIPT_STRATEGY];
    if (
      strategy &&
      SCRIPT_STRATEGIES.includes(strategy as (typeof SCRIPT_STRATEGIES)[number])
    ) {
      setScriptStrategy(strategy);
    } else {
      setScriptStrategy("async");
    }
    const position =
      projectSettings[ApiProjectSettingKey.WOOCOMMERCE_SCRIPT_POSITION];
    if (
      position &&
      SCRIPT_POSITIONS.includes(position as (typeof SCRIPT_POSITIONS)[number])
    ) {
      setScriptPosition(position);
    } else {
      setScriptPosition("head");
    }
    setServerSideTracking(
      projectSettings[
        ApiProjectSettingKey.WOOCOMMERCE_SERVER_SIDE_TRACKING_ENABLED
      ] === "true",
    );
  }, [projectSettings, scriptDomainOptions, storedBaseUrl]);

  const selectedScriptBaseUrl = useMemo(() => {
    return (
      scriptDomainOptions.find((option) => option.id === selectedUrlSource)
        ?.baseUrl || defaultScriptBaseUrl
    );
  }, [selectedUrlSource, scriptDomainOptions, defaultScriptBaseUrl]);

  const isSelectedWwwDomain = useMemo(() => {
    return (
      scriptDomainOptions.find((option) => option.id === selectedUrlSource)
        ?.isWww || false
    );
  }, [selectedUrlSource, scriptDomainOptions]);

  const previewScriptUrl = useMemo(() => {
    if (!selectedProject?.key) {
      return "";
    }
    return `${selectedScriptBaseUrl.replace(/\/+$/, "")}/${selectedProject.key}/incendium.js`;
  }, [selectedScriptBaseUrl, selectedProject?.key]);

  const onSave = useCallback(async () => {
    if (!selectedProject) {
      return;
    }
    setSaving(true);
    const nextSettings: TProjectSettings = {
      [ApiProjectSettingKey.WOOCOMMERCE_SCRIPT_BASE_URL]:
        selectedScriptBaseUrl.replace(/\/+$/, ""),
      [ApiProjectSettingKey.WOOCOMMERCE_SCRIPT_STRATEGY]: scriptStrategy,
      [ApiProjectSettingKey.WOOCOMMERCE_SCRIPT_POSITION]: scriptPosition,
      [ApiProjectSettingKey.WOOCOMMERCE_SERVER_SIDE_TRACKING_ENABLED]:
        serverSideTracking ? "true" : "false",
    };
    try {
      await Promise.all(
        Object.entries(nextSettings).map(([key, value]) =>
          saveProjectSetting(
            { key: key as ApiProjectSettingKey, value },
            selectedProject,
          ),
        ),
      );
      setProjectSettings((settings) =>
        produce(settings, (draft) => {
          Object.assign(draft, nextSettings);
        }),
      );
      dispatch(
        fetchAllClientsAndProjectsThunk({
          clientId: selectedProject.clientId as number,
          projectId: selectedProject.id as number,
        }),
      );
      showSuccessNotification(
        "Settings saved. They will be applied to your store within about 10 minutes.",
      );
      onSaved?.();
    } catch {
      showErrorNotification("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }, [
    selectedProject,
    selectedScriptBaseUrl,
    scriptStrategy,
    scriptPosition,
    serverSideTracking,
    setProjectSettings,
    dispatch,
    showSuccessNotification,
    showErrorNotification,
    onSaved,
  ]);

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" mb={1}>
        Plugin tracking
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Choose how the Incendium script is loaded on your WooCommerce store.
        Changes can take up to 10 minutes to take effect.
      </Typography>
      <Stack spacing={2}>
        <TextField
          size="small"
          label="Script loading"
          select
          value={scriptStrategy}
          onChange={(e) => setScriptStrategy(e.target.value)}
          fullWidth
        >
          {SCRIPT_STRATEGIES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Script position"
          select
          value={scriptPosition}
          onChange={(e) => setScriptPosition(e.target.value)}
          fullWidth
        >
          {SCRIPT_POSITIONS.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        {scriptDomainOptions.length > 1 && (
          <>
            <TextField
              size="small"
              label="Script domain"
              select
              value={selectedUrlSource}
              onChange={(e) => setSelectedUrlSource(e.target.value)}
              fullWidth
            >
              {scriptDomainOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {isSelectedWwwDomain && (
              <Alert severity="warning">
                You selected a www domain. This is valid, but most setups
                usually use the non-www root domain.
              </Alert>
            )}
          </>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={serverSideTracking}
              onChange={(e) => setServerSideTracking(e.target.checked)}
            />
          }
          label="Server-side tracking enabled"
        />
        {previewScriptUrl && (
          <Typography variant="caption" color="text.secondary">
            Script URL: {previewScriptUrl}
          </Typography>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={saving}
          sx={{ alignSelf: "flex-start" }}
        >
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </Stack>
    </Box>
  );
}

export default WooCommercePluginSettings;
